import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, X, Save, Languages, Hash } from 'lucide-react';
import { toast } from 'react-toastify';
import { translationApi } from '../api/api';

const SECTIONS = [
  'loginModal',
  'registerModal',
  'forgotPassword',
  'navbar',
  'footer',
  'sidebar',
  'homePage',
  'aboutPage',
  'contactPage',
  'restaurantDetail',
  'restaurantList',
  'reservationsPage',
  'profilePage',
  'reviewsPage',
  'favoritesPage',
  'notFound',
  'verifyEmail',
  'terms',
  'privacy',
  'common'
];

const SAMPLE_TRANSLATIONS = [
  { _id: '1', section: 'nav', key: 'whyAvopla', en: 'Why Avopla?', fr: 'Pourquoi Avopla?', description: 'Navigation link', updatedAt: new Date() },
  { _id: '2', section: 'nav', key: 'pricing', en: 'Pricing', fr: 'Tarification', description: 'Navigation link', updatedAt: new Date() },
  { _id: '3', section: 'footer', key: 'signIn', en: 'Sign In', fr: 'Se connecter', description: 'Footer link', updatedAt: new Date() },
];

const Translations = () => {
  const [translations, setTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingTranslation, setEditingTranslation] = useState(null);
  const [formData, setFormData] = useState({
    section: 'nav',
    key: '',
    en: '',
    fr: '',
    description: ''
  });

  useEffect(() => {
    fetchTranslations();
  }, []);

  const fetchTranslations = async () => {
    try {
      setLoading(true);
      const response = await translationApi.getAll();
      setTranslations(response.data);
    } catch (error) {
      setTranslations(SAMPLE_TRANSLATIONS);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTranslation) {
        await translationApi.update(editingTranslation.id, formData);
        toast.success('Translation updated');
      } else {
        await translationApi.create(formData);
        toast.success('Translation created');
      }
      setShowModal(false);
      setEditingTranslation(null);
      resetForm();
      fetchTranslations();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save');
    }
  };

  const handleEdit = (translation) => {
    setEditingTranslation(translation);
    setFormData({
      section: translation.section,
      key: translation.key,
      en: translation.en,
      fr: translation.fr,
      description: translation.description || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this translation?')) return;
    try {
      await translationApi.delete(id);
      toast.success('Deleted');
      fetchTranslations();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const resetForm = () => {
    setFormData({ section: 'nav', key: '', en: '', fr: '', description: '' });
  };

  const openNewModal = () => {
    setEditingTranslation(null);
    resetForm();
    setShowModal(true);
  };

  const filteredTranslations = translations.filter(t => {
    const matchesSection = activeSection === 'all' || t.section === activeSection;
    const matchesSearch = searchTerm === '' ||
      t.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.en.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.fr.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSection && matchesSearch;
  });

  const groupedTranslations = filteredTranslations.reduce((acc, t) => {
    if (!acc[t.section]) acc[t.section] = [];
    acc[t.section].push(t);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <h1>All Translations</h1>
        <p>Manage English and French translations for your website</p>
      </div>

      {/* Search & Actions */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
        <div className="search-bar" style={{ flex: 1 }}>
          <Search size={18} />
          <input
            type="text"
            placeholder="Search translations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="btn btn-primary" onClick={openNewModal}>
          <Plus size={16} />
          Add Translation
        </button>
      </div>

      {/* Section Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeSection === 'all' ? 'active' : ''}`}
          onClick={() => setActiveSection('all')}
        >
          All Sections
        </button>
        {SECTIONS.map(section => (
          <button
            key={section}
            className={`tab ${activeSection === section ? 'active' : ''}`}
            onClick={() => setActiveSection(section)}
            style={{ textTransform: 'capitalize' }}
          >
            {section}
          </button>
        ))}
      </div>

      {/* Translations */}
      {filteredTranslations.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Languages size={28} />
          </div>
          <h3>No translations found</h3>
          <p>Try adjusting your search or add a new translation</p>
          <button className="btn btn-primary" onClick={openNewModal}>
            <Plus size={16} />
            Add Translation
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {Object.entries(groupedTranslations).map(([section, items]) => (
            <div key={section}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <span className="badge badge-primary" style={{ textTransform: 'capitalize' }}>
                  {section}
                </span>
                <span style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>
                  {items.length} items
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {items.map(t => (
                  <div key={t.id} className="translation-item" style={{ margin: 0, borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                    <div className="translation-item-header">
                      <div>
                        <span className="translation-key">
                          <Hash size={12} />
                          {t.key}
                        </span>
                        {t.description && (
                          <p className="translation-description">{t.description}</p>
                        )}
                      </div>
                      <div className="translation-actions">
                        <button className="btn btn-outline btn-sm" onClick={() => handleEdit(t)}>
                          <Edit2 size={14} />
                        </button>
                        <button className="btn btn-danger btn-icon btn-sm" onClick={() => handleDelete(t.id)}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    <div className="translation-languages">
                      <div className="translation-lang">
                        <div className="translation-lang-header">
                          <span className="translation-lang-flag">🇬🇧</span>
                          English
                        </div>
                        <div className="translation-lang-text">{t.en}</div>
                      </div>
                      <div className="translation-lang">
                        <div className="translation-lang-header">
                          <span className="translation-lang-flag">🇫🇷</span>
                          French
                        </div>
                        <div className="translation-lang-text">{t.fr}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTranslation ? 'Edit Translation' : 'Add Translation'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Section</label>
                  <select
                    className="form-select"
                    value={formData.section}
                    onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                    required
                  >
                    {SECTIONS.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Key</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.key}
                    onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                    placeholder="hero.title"
                    required
                    style={{ fontFamily: 'monospace' }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">🇬🇧 English Text</label>
                  <textarea
                    className="form-textarea"
                    value={formData.en}
                    onChange={(e) => setFormData({ ...formData, en: e.target.value })}
                    placeholder="Enter English text..."
                    required
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">🇫🇷 French Text</label>
                  <textarea
                    className="form-textarea"
                    value={formData.fr}
                    onChange={(e) => setFormData({ ...formData, fr: e.target.value })}
                    placeholder="Enter French text..."
                    required
                    rows={3}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">
                    Description <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>(optional)</span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  <Save size={16} />
                  {editingTranslation ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Translations;
